import CircleIcon from '@mui/icons-material/Circle';
import { FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { updateDashboardFunc } from "../../../lib/helper";
import theme from "../../../theme";
import { ColorOptions } from "../../../theme/colors";
import { Dashboard } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    title: {
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    formContainer: {
        minWidth: '105px',
        padding: 0,
        border: 0,
        "& > .MuiInputBase-root":{
            height: '30px',
            width: 'auto',
            borderRadius: '24px',
            "& > .MuiSvgIcon-root": {
                width: 28,
                height: 28,
            },
            "& fieldset": {
                border: 0,
            },
        },
    },
    menuItem: {
        width: '100%',
    },
    badge: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '30px',
        width: 'auto',
        padding: '0 4px',
        borderRadius: 24,
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: '500',
        textTransform: 'capitalize',
    },
}));

const DashboardStatusMenu: FC<{
    dashboard: Dashboard,
    withTitle?: boolean,
    readOnly?: boolean,
}> = ({ dashboard, withTitle, readOnly }) => {
    const classes = useStyles();
    const { statuses } = useContext(GroupSettingsContext);
    const [selected, setSelected] = useState<string>('');

    const palettes = useMemo(() => Array.from(new Set(Object.values(ColorOptions)
        .filter(color => !['neutral', 'brown', 'lime', 'pink', 'red', 'shades'].includes(color)))), []);

    const getColors = useCallback((title: string, withBorder?: boolean) => {
        const colorMap: Record<string, { bg: string; text: string; border: string }> = {
            'New':   { bg: '#F0FDFA', text: '#0D9488', border: '#99F6E4' }, // teal-50 / teal-600 / teal-200
            'Lead':  { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' }, // blue-50 / blue-600 / blue-200
            'Tier 1': { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' }, // orange-50 / orange-600 / orange-200
            'Tier 2': { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' }, // amber-50 / amber-600 / amber-200
            'Tier 3': { bg: '#FEFCE8', text: '#CA8A04', border: '#FEF08A' }, // yellow-50 / yellow-600 / yellow-200
            'Won':   { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' }, // green-50 / green-600 / green-200
            'Lost':  { bg: '#F9FAFB', text: '#4B5563', border: '#E5E7EB' }, // gray-50 / gray-600 / gray-200
        };

        const key = Object.keys(colorMap).find(
            k => k.toLowerCase() === title.toLowerCase()
        );

        if (key) {
            const { bg, text, border } = colorMap[key];

            return {
                backgroundColor: bg,
                dotColor: border,
                color: text,
                border: withBorder ? `1px solid ${border}` : 'inherit',
            };
        }

        const index = statuses.findIndex(
            s => s.toLowerCase() === title.toLowerCase()
        );

        if (index !== -1 && index < palettes.length) {
            return {
                backgroundColor: theme.colors[palettes[index]][50],
                dotColor: theme.colors[palettes[index]][400],
                color: theme.colors[palettes[index]][700],
                border: withBorder
                    ? `1px solid ${theme.colors[palettes[index]][200]}`
                    : 'inherit',
            };
        }

        return {
            backgroundColor: theme.colors.neutral[100],
            dotColor: theme.colors.neutral[400],
            color: theme.colors.neutral[600],
            border: withBorder
            ? `1px solid ${theme.colors.neutral[500]}`
            : 'inherit',
        };
    }, [statuses, palettes]);

    const handleChange = useCallback(async (newStatus: string) => {
        const { tagGroups, status, ...props } = dashboard;

        setSelected(newStatus);
        await updateDashboardFunc({ ...props,
            status: newStatus === dashboard.status ? '<rename this status>' : newStatus,
            tagGroups: tagGroups?.map(({title, tags}) => ({title, tags})) ?? [],
        });
    }, [dashboard]);

    useEffect(()=>{
        setSelected(dashboard.status);
    }, [dashboard.status]);

    return (<>
        <Stack direction="column" spacing={1} width="fit-content" height="fit-content">
            {withTitle && (<Typography className={classes.title}> {'Status'} </Typography>)}
            {readOnly ? (
                <Typography className={classes.badge}
                    sx={{ ...getColors(selected, true) }}>
                    {selected || 'N/A'}
                </Typography>
            ) : (<>
                <FormControl className={classes.formContainer} variant="outlined"
                    sx={{ "& > .MuiInputBase-root":{ ...getColors(selected, true) } }}>
                    <Select value={selected}
                        onChange={(e) => handleChange(e.target.value as string)}
                        renderValue={(selected: string) => (
                            <Typography className={classes.badge}> {selected} </Typography>
                        )} MenuProps={{ PaperProps: { sx: { borderRadius: 2 }} } }>
                        {statuses.map((item, i) => (
                            <MenuItem className={classes.menuItem}
                                value={item}
                                key={'dashboard-status-menu-170-' + i}>
                                <CircleIcon sx={{ width: 12, height: 12, color: getColors(item)?.dotColor || '#000' }} />
                                <Typography className={classes.badge} ml={1}> {item} </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </>)}
        </Stack>
    </>);
}

export default DashboardStatusMenu;